import { useQuery, UseQueryOptions } from 'react-query';
import { authFetch } from '../../CommonUtils/authFetch';
import { urlWithParams } from '../../CommonUtils/urlWithParams';
import { PerformancePeriodDTO } from '../types';
import { dateToISO8601 } from '../helpers/dates';
import { QueryFeature } from '../../../config/queryClient';

interface ItemDTO {
  isHighlighted: boolean;
  name: string;
  type: 'Percent' | 'Currency';
  value: number;
  isFromOrionSetting: boolean;
}

interface PerformanceSummaryDTO {
  items: ItemDTO[];
}

interface PerformanceVsBenchmarkDTO {
  name: string;
  color: string | null;
  data: { date: string; value: number }[];
}

interface TransactionsSummaryDTO {
  items: ItemDTO[];
}

interface PerformanceDTO {
  performanceSummary: PerformanceSummaryDTO;
  performanceVsBenchmark: PerformanceVsBenchmarkDTO[];
  transactionsSummary: TransactionsSummaryDTO;
  portfolioPeriodReturn: number | null;
}

interface FetchPerformanceParams {
  clientId: string;
  period?: PerformancePeriodDTO;
  startDate?: string;
  endDate?: string;
  accountId: string;
  groupId: string;
}

const fetchPerformance = async ({
  clientId,
  period,
  startDate,
  endDate,
  accountId,
  groupId,
}: FetchPerformanceParams): Promise<PerformanceDTO> =>
  authFetch(
    urlWithParams(
      '/Consumer/OrionAccountsWidgets/Performance',
      {
        clientId,
        period,
        startDate,
        endDate,
        accountId: accountId || null,
        groupId: groupId || null,
        localDate: dateToISO8601(),
        maxSamples: 30,
      },
      { skipNulls: true },
    ),
  );

export const PERFORMANCE_QUERY_KEY = 'performance';

interface UsePerformanceQueryParams {
  clientId: string;
  period?: PerformancePeriodDTO;
  startDate: string;
  endDate: string;
  accountId: string;
  groupId: string;
}

export const usePerformanceQuery = (
  {
    clientId,
    period,
    startDate: _startDate,
    endDate: _endDate,
    accountId,
    groupId,
  }: UsePerformanceQueryParams,
  options?: UseQueryOptions<PerformanceDTO, Error>,
) => {
  const startDate = period === undefined ? _startDate : undefined;
  const endDate = period === undefined ? _endDate : undefined;

  return useQuery(
    [
      QueryFeature.AccountPerformanceReporting,
      PERFORMANCE_QUERY_KEY,
      clientId,
      period,
      startDate,
      endDate,
      accountId,
      groupId,
    ],
    () => fetchPerformance({ clientId, period, startDate, endDate, accountId, groupId }),
    {
      ...(period === undefined ? { cacheTime: 0 } : {}), // Do not cache custom ranges
      ...options,
    },
  );
};
